import { TranslatedText } from "@/components/common/text";
import { cn } from "@/utils/css";
import { BenchmarkChart } from "./BenchmarkChart";

const formatCurrency = (value) => `$${Math.round(value).toLocaleString("en-US")}`;

const ComparisonPill = ({ change, label }) => {
    const positive = change > 0;
    return (
        <span
            className={cn("flex items-center gap-1 whitespace-nowrap rounded-[34px] py-0.5 pl-2 pr-2.5 text-xs", {
                "bg-cm-p-container-green text-cm-p-on-container-green": positive,
                "bg-cm-p-surface-hover text-cm-p-text-secondary": !positive,
            })}
        >
            <span className="font-semibold tracking-[0.24px]">
                {positive ? "+" : ""}
                {change}%
            </span>
            <TranslatedText>{label}</TranslatedText>
        </span>
    );
};

const LEGEND = [
    { key: "company", label: "Company", swatch: "h-0.5 w-2 rounded-[30px] bg-cm-p-accent-500" },
    { key: "market", label: "Market average", swatch: "size-1.5 rounded-[2px] bg-cm-p-graph-market-average" },
    { key: "topPerforming", label: "High performing companies", swatch: "size-1.5 rounded-[2px] bg-cm-p-graph-high-perf" },
];

const ChartLegend = ({ skeleton }) => (
    <div
        className={cn(
            "flex flex-wrap items-center justify-center gap-4 text-xs tracking-[0.24px] text-cm-p-text-secondary",
            skeleton && "opacity-40",
        )}
    >
        {LEGEND.map(({ key, label, swatch }) => (
            <div key={key} className="flex items-center gap-1">
                <span className={swatch} />
                <TranslatedText>{label}</TranslatedText>
            </div>
        ))}
    </div>
);

// Placeholder for the headline row of a metric that is not configured yet.
const SkeletonHeadline = () => (
    <div className="flex h-[38px] items-center gap-4">
        <div className="h-[25px] w-[75px] rounded-[4px] bg-cm-p-surface-container" />
        <div className="flex items-center gap-1">
            <div className="h-[23px] w-[86px] rounded-[34px] bg-cm-p-container-green" />
            <div className="h-[23px] w-[123px] rounded-[34px] bg-cm-p-surface-container" />
        </div>
    </div>
);

export const MetricCard = ({ title, badge, metric, yTicks, skeleton = false, className }) => (
    <div
        className={cn("flex w-full flex-col gap-5 rounded-[6px] border border-cm-p-outline-variation bg-cm-p-surface px-6 py-5", className)}
    >
        <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
                <h3 className="text-base font-medium text-cm-p-text-primary">
                    <TranslatedText>{title}</TranslatedText>
                </h3>
                {badge ? (
                    <TranslatedText className="rounded-[34px] bg-cm-p-surface-container px-2 py-0.5 text-[11px]/[16.5px] italic text-cm-p-text-primary">
                        {badge}
                    </TranslatedText>
                ) : null}
            </div>
            {skeleton ? (
                <SkeletonHeadline />
            ) : (
                <div className="flex flex-wrap items-center gap-4">
                    <span className="text-2xl font-semibold tracking-[0.48px] text-cm-p-text-primary">
                        {formatCurrency(metric.currentCompanyValue)}
                    </span>
                    <div className="flex flex-wrap items-center gap-1">
                        <ComparisonPill change={metric.percentageVsMarket} label="than market avg" />
                        <ComparisonPill change={metric.percentageVsTopPerforming} label="than high performers" />
                    </div>
                </div>
            )}
        </div>
        <div className="flex flex-col items-center gap-5">
            <BenchmarkChart metric={metric} yTicks={yTicks} skeleton={skeleton} />
            <ChartLegend skeleton={skeleton} />
        </div>
    </div>
);
